import { useTranslation } from 'next-i18next'
import { FC, useEffect, useState } from 'react'

import { InfoTooltip } from 'ui'
import Filter from './Filter'
import PriceFilterFieldset from './PriceFilterFieldset'

import { abbreviateCurrency } from '../../utils/abbreviateCurrency'

type PriceFilterProps = {
  min: number | null
  max: number | null
  inline?: boolean
  onDone?: (min: number | null, max: number | null) => void
  setMin?: (min: number | null) => void
  setMax?: (max: number | null) => void
}

export const formatButtonLabel = (
  t: (key: string, vars?: object) => string,
  min: number,
  max: number
) => {
  if (!min && !max) {
    return t('ui.any-price')
  }

  if (min && !max) {
    return t('ui.min-price-label', { min: abbreviateCurrency(min) })
  }

  if (!min && max) {
    return t('ui.max-price-label', { max: abbreviateCurrency(max) })
  }

  return t('ui.min-and-max-price-label', {
    min: abbreviateCurrency(min),
    max: abbreviateCurrency(max),
  })
}

const PriceFilter: FC<PriceFilterProps> = ({
  min = null,
  max = null,
  inline = false,
  onDone = () => null,
  setMin = () => null,
  setMax = () => null,
}) => {
  const { t } = useTranslation(['common', 'homes'])
  const [label, setLabel] = useState(
    formatButtonLabel(t, Number(min), Number(max))
  )
  const [localMin, setLocalMin] = useState<number | null>(min)
  const [localMax, setLocalMax] = useState<number | null>(max)

  const onClose = () => {
    setLabel(formatButtonLabel(t, Number(localMin), Number(localMax)))
    onDone(localMin, localMax)
  }

  useEffect(() => {
    setLocalMin(min)
    setLocalMax(max)
    setLabel(formatButtonLabel(t, Number(min), Number(max)))
  }, [min, max, t])

  return inline ? (
    <div>
      <span className="font-semibold">{t('ui.list-price')}</span>
      <InfoTooltip side="right" className="relative ml-8">
        {t('list-price-info', { ns: 'homes' })}
      </InfoTooltip>
      <PriceFilterFieldset
        min={min}
        setMin={setMin}
        max={max}
        setMax={setMax}
      />
    </div>
  ) : (
    <Filter
      heading={t('ui.list-price')}
      tooltip={t('list-price-info', { ns: 'homes' })}
      buttonText={label}
      onOpenChange={(open) => (!open ? onClose() : null)}
      onClear={() => {
        setLocalMin(null)
        setLocalMax(null)
      }}
      isActive={!!min || !!max}
    >
      <PriceFilterFieldset
        min={localMin}
        setMin={setLocalMin}
        max={localMax}
        setMax={setLocalMax}
        autofocus
      />
    </Filter>
  )
}

export default PriceFilter
